import { isBefore, parse } from 'date-fns';
import { isNumber } from 'lodash';

import type { GetSentimentDataQuery } from '@revelio/data-access';

import { topics } from '../topics-subfilter/topics';
import type { SentimentBarChartData } from '../types';

const emptyData = {
  overall: [],
  business_outlook: [],
  career_growth: [],
  compensation: [],
  benefits: [],
  healthcare: [],
  retirement_pension: [],
  childcare: [],
  education_assistance: [],
  clients_and_customers: [],
  wellness_programs: [],
  work_life_balance: [],
  diversity_and_inclusion: [],
  leadership: [],
  culture: [],
  technology: [],
  commission: [],
  remote_work: [],
  onboarding: [],
  mentorship: [],
  promotion_opportunities: [],
  paid_time_off: [],
  transparency: [],
  efficiency_and_bureaucracy: [],
  product_and_service_quality: [],
  commitment: [],
  prestige: [],
  coworkers: [],
  mission_and_vision: [],
  workplace_facilities: [],
  commute: [],
  training: [],
  company_growth: [],
  schedule_flexibility: [],
  job_security: [],
  compliance: [],
  ethics: [],
  family_support: [],
  independence: [],
  safety: [],
  harassment: [],
  honesty: [],
  integrity: [],
  internal_communication: [],
  office_politics: [],
  overtime: [],
};

const isDateBeforeMinimumDate = (date: string, minDate: string): boolean => {
  return isBefore(
    parse(date, 'yyyy-MM', new Date()),
    parse(minDate, 'yyyy-MM', new Date())
  );
};

export const getTimeLimitedStartDate = (
  startDate: string | null,
  minStartDate: string
): string | null => {
  if (!startDate) return minStartDate;

  return isDateBeforeMinimumDate(startDate, minStartDate)
    ? minStartDate
    : startDate;
};

export const getRoundedCount = (weight: number | null | undefined): number => {
  if (!isNumber(weight) || Number(weight) === 0) return 0;

  return Math.max(1, Math.round(weight));
};

type SentimentEntityScores = NonNullable<
  NonNullable<NonNullable<GetSentimentDataQuery['sentiment']>[number]>['result']
>['scores'];

type SentimentMonthRef = NonNullable<
  NonNullable<GetSentimentDataQuery['sentiment']>[number]
>['months_refs'];

type BarEntry = {
  date?: string;
  label: string;
  count: number;
  value: number | null;
};

const extractSnapshotScores = (
  scores: NonNullable<SentimentEntityScores>,
  label: string
): Record<string, BarEntry> => {
  const entries: Record<string, BarEntry> = {};

  topics.forEach((topic) => {
    const score = scores[topic.value];
    if (!score) return;

    const first = score[0];
    if (!first) return;

    entries[topic.value] = {
      label,
      count: getRoundedCount(first.weight),
      value: isNumber(first.value) ? first.value / 100 : null,
    };
  });

  return entries;
};

const extractTimeseriesScores = (
  scores: NonNullable<SentimentEntityScores>,
  monthsRefs: SentimentMonthRef,
  label: string
): Record<string, BarEntry> => {
  const monthLookup = (monthsRefs ?? []).reduce<Record<number, string>>(
    (acc, month_ref) => {
      if (!month_ref || !month_ref.id || !month_ref.longName) return acc;
      acc[month_ref.id] = month_ref.longName;
      return acc;
    },
    {}
  );

  const entries: Record<string, BarEntry> = {};

  topics.forEach((topic) => {
    const score = scores[topic.value];
    if (!score) return;

    const last = score[score.length - 1];
    if (!last || !last.month_id) return;

    entries[topic.value] = {
      date: monthLookup[last.month_id],
      label,
      count: getRoundedCount(last.weight),
      value: isNumber(last.value) ? last.value / 100 : null,
    };
  });

  return entries;
};

export const normalizeBarScoresData = (
  data: GetSentimentDataQuery,
  isSnapshot = false
): SentimentBarChartData => {
  if (!data?.sentiment) return emptyData;

  const emptyDataSet = topics.reduce<SentimentBarChartData>((acc, topic) => {
    acc[topic.value] = [];
    return acc;
  }, emptyData);

  const initialResult = data.sentiment.reduce<SentimentBarChartData>(
    (acc, entity) => {
      if (!entity?.metadata || !entity?.result?.scores) {
        return acc;
      }

      if (!isSnapshot && !entity.months_refs) {
        return acc;
      }

      const label = entity.metadata?.shortName;
      if (!label) return acc;

      const entries = isSnapshot
        ? extractSnapshotScores(entity.result.scores, label)
        : extractTimeseriesScores(
            entity.result.scores,
            entity.months_refs,
            label
          );

      topics.forEach((topic) => {
        const entry = entries[topic.value];
        if (entry) {
          acc[topic.value] = [...acc[topic.value], entry];
        }
      });

      return acc;
    },
    emptyDataSet
  );

  // Filter out topics where all entries have null values
  return Object.entries(initialResult).reduce<SentimentBarChartData>(
    (acc, [topic, entries]) => {
      const allNull = entries.every((entry) => entry.value === null);
      acc[topic as keyof SentimentBarChartData] = allNull ? [] : entries;
      return acc;
    },
    {} as SentimentBarChartData
  );
};
